/** @format */

import React, { useState, useEffect } from "react";
import { Col, Card, CardBody, Button, Badge, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import { FaEdit, FaTrash } from "react-icons/fa";
import CommonCardHeader from "../../../Utils/CommonComponents/CommonCardHeader";
import FilterComponent from "../../Common/FilterComponent";
import { getAPIURL, getVendorID, getVendorSettings, setVendorData } from "../../../services/helper";
import http from "../../../services/http";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../../ReduxToolkit/Reducers/AppReducer";
import { setPosItems } from "../../../ReduxToolkit/Reducers/CartReducer";
import { Link } from "react-router-dom";
import SvgIcon from "../../../Utils/CommonComponents/CommonIcons/CommonSvgIcons";
import { BsCopy } from "react-icons/bs";
import AddonModalForm from "./AddonForm";

const AddonsList = () => {
  const [filterText, setFilterText] = useState("");
  const [searchKeywords, setSearchKeywords] = useState([]);
  const [addonsList, setAddons] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState({
    productmeta_id: "new",
    productmeta_name: "",
    the_meta: {},
  });
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.products);

  useEffect(() => {
    const loadAddons = async () => {
      try {
        const { data: addonsResp } = await http.get(
          `${api_url}/addons?vendor_id=${vendorId}`
        );

        const parsedAddons = parsedMeta(addonsResp);
        setAddons(parsedAddons);
      } catch (error) {
        console.error(
          "Error fetching addons:",
          error.response ? error.response.data : error.message
        );
      }
    };

    loadAddons();
  }, []);

  const openModalForEdit = (addon) => {
    setSelectedAddon(addon);
    setModal(true);
  };

  const openModalForNew = () => {
    setModal(true);
  };

  const toggleModal = () => setModal(!modal);

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert2 correctly
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true, // You must set showCancelButton to true to show both buttons
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    // If the user confirms deletion
    if (result.isConfirmed) {
      // Use isConfirmed to check if the confirm button was clicked
      try {
        dispatch(setLoading(true));
        const { data } = await http.post(`${api_url}/delete-addon/`, { id });
        const { message, success } = data;
        if (success) {
          setAddons(addonsList.filter((addon) => addon.productmeta_id !== id));
          toast.info("Addon deleted successfully.");
        }
        dispatch(setLoading(false));
      } catch (error) {
        console.error("Error deleting addon:", error);
        toast.error("Failed to delete the addon.");
        dispatch(setLoading(false));
      }
    }
  };

  const handleDupliateProduct = async (addon) => {
    const result = await Swal.fire({
      title: "Duplicate new addon?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--theme-red)",
      cancelButtonColor: "var(--theme-blue)",
      confirmButtonText: "Yes, Duplicate it!",
    });
    if (result.isConfirmed) {
      try {
        const new_addon = { ...addon };
        delete new_addon.productmeta_id;
        handleSave(new_addon);
      } catch (error) {
        toast.error("Error duplicating addon");
        console.log(error);
      }
    }
  };

  const parsedMeta = (addons) => {
    return addons.map((addon) => {
      let parsedMeta = {};
      try {
        parsedMeta = JSON.parse(addon.the_meta || "{}");
      } catch (error) {
        console.error("Error parsing the_meta field", error);
      }

      return {
        ...addon,
        the_meta: parsedMeta,
      };
    });
  };

  const handleSave = async (row) => {
    try {
      dispatch(setLoading(true));
      setModal(false);

      row = { ...row, vendor_id: vendorId };
      const api_url = getAPIURL();
      // console.log(row);
      const { data } = await http.post(`${api_url}/addons`, row);
      const { addons } = data;
      const parsedAddons = parsedMeta(addons);
      setAddons(parsedAddons);
      
      // Update pos_items for products that use this addon
      const updatedAddonId = String(row.productmeta_id);
      const itemsString = getVendorSettings("pos_items");
      let pos_items = [];
      
      try {
        pos_items = JSON.parse(itemsString) || [];
      } catch (err) {
        console.warn("Failed to parse pos_items:", err);
      }
      
      // Update addon data in pos_items
      let hasUpdates = false;
      pos_items.forEach(item => {
        if (item.ppom_id && item.addons) {
          const ppomIds = Array.isArray(item.ppom_id) ? item.ppom_id : [item.ppom_id];
          if (ppomIds.includes(updatedAddonId)) {
            // Replace addons that belong to this addon group (same data_name)
            const updatedAddons = Object.values(row.the_meta);
            const addonGroupDataNames = updatedAddons.map(addon => addon.data_name);
            
            // Keep addons that don't belong to this group, replace those that do
            item.addons = item.addons.filter(existingAddon => 
              !addonGroupDataNames.includes(existingAddon.data_name)
            ).concat(updatedAddons);
            
            hasUpdates = true;
            console.log(`Updated addons for product ${item.id}:`, item.addons);
          }
        }
      });
      
      if (hasUpdates) {
        await http.saveVendorData({ pos_items: JSON.stringify(pos_items) });
        setVendorData({ pos_items: JSON.stringify(pos_items) });
        dispatch(setPosItems(pos_items));
        console.log('pos_items updated successfully');
      } else {
        console.log('No pos_items updates needed');
      }
      
      toast.success("Addons Saved");
      setSelectedAddon({
        productmeta_id: "new",
        productmeta_name: "",
        the_meta: {},
      });
      dispatch(setLoading(false));
      // window.location.reload();
    } catch (error) {
      dispatch(setLoading(false));
      console.error("Error deleting addon:", error);
    }
  };

  const columns = [
    {
      name: "ID",
      selector: (row) => row.productmeta_id,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.productmeta_name,
      sortable: true,
    },
    {
      name: "Created",
      selector: (row) => row.productmeta_created,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="product-action">
          <BsCopy
            role="button"
            className="font-hover me-1"
            onClick={() => handleDupliateProduct(row)}
          />
          <Link onClick={() => openModalForEdit(row)}>
            <SvgIcon iconId="edit-content" />
          </Link>

          <SvgIcon
            iconId="trash1"
            onClick={() => handleDelete(row.productmeta_id)}
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const handleKeywordSearch = (e) => {
    if (e.key === 'Enter' && filterText.trim()) {
      const newKeyword = filterText.trim().toLowerCase();
      if (!searchKeywords.includes(newKeyword)) {
        setSearchKeywords([...searchKeywords, newKeyword]);
      }
      setFilterText("");
    }
  };

  const removeKeyword = (keyword) => {
    setSearchKeywords(searchKeywords.filter(k => k !== keyword));
  };

  const filteredAddons = addonsList.filter((item) => {
    // If no search keywords and no filter text, show all
    if (searchKeywords.length === 0 && !filterText.trim()) return true;
    
    const itemText = `${item.productmeta_name || ''} ${JSON.stringify(item.the_meta || {})}`.toLowerCase();
    
    // Check both active keywords and current filter text
    const allSearchTerms = [...searchKeywords];
    if (filterText.trim()) {
      allSearchTerms.push(filterText.trim().toLowerCase());
    }
    
    return allSearchTerms.every(keyword => itemText.includes(keyword));
  });

  return (
    <Col sm={12}>
      <Card>
        <CommonCardHeader title="Add Addons" />
        <CardBody>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="flex-grow-1 me-3">
                <Input
                  placeholder="Type keyword and press Enter to add filter..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  onKeyDown={handleKeywordSearch}
                />
              </div>
              <Button color="primary" onClick={openModalForNew}>
                Add New Addon
              </Button>
            </div>
            
            {searchKeywords.length > 0 && (
              <div className="mb-2">
                <small className="text-muted me-2">Active filters:</small>
                {searchKeywords.map(keyword => (
                  <Badge 
                    key={keyword} 
                    color="primary" 
                    className="me-1 mb-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => removeKeyword(keyword)}
                  >
                    {keyword} ×
                  </Badge>
                ))}
                <Button 
                  color="link" 
                  size="sm" 
                  className="p-0 ms-2"
                  onClick={() => setSearchKeywords([])}
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
          <DataTable data={filteredAddons} columns={columns} pagination />
        </CardBody>
      </Card>
      {selectedAddon && (
        <AddonModalForm
          isOpen={modal}
          toggle={toggleModal}
          originalRow={selectedAddon}
          onSave={handleSave}
          addonsList={addonsList}
        />
      )}
    </Col>
  );
};

export default AddonsList;
